import useCartStore from '~stores/cart';
/**
 * Go through each discount object and determine if type is excluded.
 *  @params {Object} entry - Discount object.
 *  @params {Object} logic - Product object.
 * - Exclusions override inclusions.
 * - If an earlier type is excluded then skip checking and return false.
 * - If an earlier type is included then continue checking exclusions to
 *   make sure a later type doesn't mean the discount is excluded.
 */
export function checkIncludedOrExcludedProducts(entry, logic) {
  let excluded = false;
  let included = false;

  if (logic?.collectionIds?.length) {
    excluded = entry.exclude.collectionIds?.some((id) => {
      return logic.collectionIds.includes(Number(id));
    });

    included = entry.include.collectionIds?.some((id) => {
      return logic.collectionIds.includes(Number(id));
    });
  }

  if (logic?.productId && !excluded) {
    excluded = entry.exclude.productIds?.some((id) => {
      return logic.productId === Number(id);
    });

    if (!included) {
      included = entry.include.productIds?.some((id) => {
        return logic.productId === Number(id);
      });
    }
  }

  if (logic?.variantId && !excluded) {
    excluded = entry.exclude.variantIds?.some((id) => {
      return logic.variantId === Number(id);
    });

    if (!included) {
      included = entry.include.variantIds?.some((id) => {
        return logic.variantId === Number(id);
      });
    }
  }

  if (excluded) {
    return false;
  }

  return included;
}

/**
 * Compute array of valid campaign codes.
 * @returns {Array}
 */
export function validCampaignCodes() {
  return cnvs.discount.campaigns
    .filter((campaign) => campaign.type !== 'influencer')
    .map((campaign) => campaign.code.map((code) => code.toUpperCase()))
    .flat();
}

export function validOtherCampaignCodes() {
  return cnvs.discount.campaignCodes.map((code) => code.toUpperCase());
}

/**
 * Compute array of valid influencer codes.
 * @returns {Array}
 */
export function validInfluencerCodes() {
  return cnvs.discount.influencers.map((code) => code.toUpperCase());
}

export function validPrivateCodes() {
  return cnvs.discount.privateCampaigns
    .map((campaign) => campaign.code.toUpperCase())
    .flat();
}

export function getTypes(code) {
  const upperCode = code.toUpperCase();

  if (validPrivateCodes().includes(upperCode)) {
    return 'priv';
  }

  if (
    validOtherCampaignCodes().includes(upperCode) ||
    validCampaignCodes().includes(upperCode)
  ) {
    return 'pub';
  }

  if (validInfluencerCodes().includes(upperCode)) {
    return 'infl';
  }

  // check if the code is an order type discount
  // order type discounts are fixed amount discounts that are applied to the entire order
  const cartStore = useCartStore();
  const orderType = cartStore.response.cart_level_discount_applications.find(
    (app) =>
      app.title.toUpperCase() === upperCode &&
      app.target_selection === 'all' &&
      app.value !== '0.0' &&
      app.total_allocated_amount !== 0,
  );

  if (orderType) {
    return 'order';
  }

  const productType = getProductCode(upperCode);
  if (productType) {
    return 'product';
  }

  // if it stays empty, it must be shipping type discount or invalid
  return 'shipping';
}

export function getProductCode(code) {
  const cartStore = useCartStore();
  const codeInCartLevel =
    cartStore.response.cart_level_discount_applications.find((app) => {
      return app.title.toUpperCase() === code;
    });

  const cartItemsDiscounts = cartStore.response.items.map((item) => {
    return item.discounts.map((discount) => {
      return discount.title.toUpperCase();
    });
  });

  const hasProductDiscount = cartItemsDiscounts
    ? cartItemsDiscounts.some((discount) => {
        return discount.includes(code);
      })
    : false;

  if (!codeInCartLevel && hasProductDiscount) {
    return 'product';
  }

  return '';
  // line_level_discount_allocations
}

/**
 * Get the first valid campaign code from an array of codes.
 * @param {Array} codes - Array of discount codes.
 * @returns {String|null} - The first valid campaign code or null if none found.
 */
export function getCampaignCode() {
  const cartStore = useCartStore();
  const codes = cartStore.response.discount_codes.map((discount) =>
    discount.code.toUpperCase(),
  );

  const campaignTypes = ['pub', 'infl', 'priv'];
  const campaignCode = codes.find((code) => {
    const type = getTypes(code);
    return campaignTypes.includes(type);
  });
  return campaignCode;
}

export function isOrderCode(code) {
  return getTypes(code) === 'order';
}

/**
 * Check if a discount applies to the current code
 * @param {Object} discount - Discount entry with applyTo and specificInflCodes/specInflCodes
 * @param {String} currentCode - Currently active discount code
 * @returns {Boolean}
 */
export function isDiscountApplicableForCode(discount, currentCode) {
  if (!discount.applyTo || discount.applyTo === 'allCodes') {
    return true;
  }

  const codeUpper = currentCode?.toUpperCase() || '';

  if (discount.applyTo === 'specificInflCodes') {
    // Handle string format (comma-separated) for regular discounts
    const codes = (discount.specificInflCodes || '')
      .split(',')
      .map((discountCode) => discountCode.trim().toUpperCase())
      .filter(Boolean);
    return codes.includes(codeUpper);
  }

  if (discount.applyTo === 'inflCodes') {
    return validInfluencerCodes().includes(codeUpper);
  }

  // Handle array format for conditionalDiscounts (specInflCodes)
  if (Array.isArray(discount.specInflCodes) && discount.specInflCodes.length) {
    return discount.specInflCodes
      .map((discountCode) => discountCode.toUpperCase())
      .includes(codeUpper);
  }

  return true;
}
